import { yupResolver } from '@hookform/resolvers/yup';
import { Error } from '@lmt/unicorn-shared/app/components/common/Error';
import {
  CreateDoctorDto,
  DoctorType,
  QueryFactory,
  ResourceType,
} from '@lmt/shared-types';

import {
  CustomDictionaryName,
  useActiveDictionaryValuesByTitle,
  useDictionaryByTitle,
} from '@lmt/unicorn-shared/app/api/custom-hooks';

import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { FieldBlock } from '@lmt/unicorn-shared/app/components/common/FieldBlock';
import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { SubmitButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/SubmitButton';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { HookFormDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import { Input } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/Input';
import { doctorDataYupSchema } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/common/common';
import localStyles from '@lmt/unicorn-shared/app/components/doctors/doctor-form/DoctorCreatingOrEditingForm.module.scss';
import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import { FormComponentProps } from '@lmt/unicorn-shared/app/components/global-forms/GlobalForms.types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { useAdvancedForm } from '@lmt/unicorn-shared/app/helpers/form/advanced-form';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEventCallback } from '@mui/material';
import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { DoctorData } from './common/types';
import { isEmpty } from 'lodash';

export interface EditDoctorProps {
  doctorType: DoctorType;
  doctorId: number | null | undefined;
  showResourceInfo?: boolean;
}
const defaultDoctorData: Partial<CreateDoctorDto> = {
  active: true,
};

export const DoctorCreatingOrEditingForm: FC<
  FormComponentProps & EditDoctorProps
> = (props) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    loadBlocking,
    doctorType,
    doctorId: propsDoctorId,
    registerFormDirtyStateRetriever,
  } = props;
  const [doctorId, setDoctorId] = useState(propsDoctorId);
  const claims = useClaims();
  const doctorQuery = QueryFactory.DoctorQuery.useGetDoctorQuery(
    props.doctorId!,
    { enabled: !!props.doctorId },
  );
  const resourceEnabled =
    claims.permissions.manageResources &&
    doctorType === DoctorType.Clinic &&
    !!props.showResourceInfo;
  const resourceQuery = QueryFactory.ResourceQuery.useGetResourceQuery(
    doctorQuery.data?.resourceId!,
    {
      enabled: resourceEnabled && !!doctorQuery.data?.resourceId,
    },
  );
  const defaultUsedAsResource = !!doctorQuery.data?.resourceId;

  const doctorDictionary = useDictionaryByTitle(CustomDictionaryName.Doctors);
  const { data: personTitles = [] } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.Titles,
  );
  const { data: specialties = [] } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.Specialties,
  );
  const { data: clinics = [] } = useActiveDictionaryValuesByTitle(
    CustomDictionaryName.Clinics,
  );
  const i18nextDict = useTranslation(NS_DataBlockFields);

  const form = useAdvancedForm<DoctorData>(
    useEventCallback(async (data) => {
      await tryCompleteForm(formType, formId, async () => {
        let createdDoctor = null;
        if (doctorId) {
          await QueryFactory.DoctorClient.editDoctor(doctorId, data);
        } else {
          createdDoctor = await QueryFactory.DoctorClient.createDoctor(data);
          setDoctorId(createdDoctor.id);
        }
        if (
          claims.permissions.manageResources &&
          (defaultUsedAsResource !== data.isUsedAsResource ||
            resourceQuery.data?.name !== resourceName)
        ) {
          const resourceId = doctorQuery.data?.resourceId;
          if (data.isUsedAsResource) {
            if (resourceId) {
              const dto = {
                name: data.resourceName,
              };
              await QueryFactory.ResourceClient.editResource(resourceId, dto);
              await queryClient.invalidateQueries({
                queryKey:
                  QueryFactory.ResourceQuery.getResourceQueryKey(resourceId),
              });
            } else {
              const dto = {
                name: data.resourceName,
                type: ResourceType.Doctors,
                doctorId: doctorId ? doctorId : (createdDoctor?.id ?? null),
              };
              await QueryFactory.ResourceClient.createResource(dto);
            }
          } else if (doctorQuery.data?.resourceId) {
            await QueryFactory.ResourceClient.removeResource(
              doctorQuery.data.resourceId,
            );
          }
        }
        await queryClient.invalidateQueries({
          queryKey: QueryFactory.ResourceQuery.getResourcesQueryKey(),
        });
        if (doctorId) {
          await queryClient.invalidateQueries({
            queryKey: QueryFactory.DoctorQuery.getDoctorQueryKey(doctorId),
          });
        }
        if (createdDoctor) {
          return createdDoctor;
        }
      });
    }),
    useMemo(() => {
      return {
        ...(props.doctorId ? doctorQuery.data : (defaultDoctorData as any)),
        doctorType: doctorType,
        resourceName: resourceQuery.data?.name ?? '',
        isUsedAsResource: !!doctorQuery.data?.resourceId,
      };
    }, [doctorQuery.data, resourceQuery.data]),
    {
      resolver: yupResolver(doctorDataYupSchema),
      shouldFocusError: true,
      criteriaMode: 'all',
    },
  );

  const queryClient = useQueryClient();
  const modals = useModal();
  const i18n = useScopedTranslation('doctors.form');

  const { dirtyFields } = form.formState;
  const { watch, setValue, getValues } = form;
  const [resourceName, isUsedAsResource] = watch([
    'resourceName',
    'isUsedAsResource',
  ]);
  useEffect(
    function setDefaultResourceName() {
      if (isUsedAsResource && isEmpty(resourceName)) {
        const name = getValues().name?.trim() ?? '';
        const surname = getValues().surname?.trim() ?? '';
        const doctorFullName = `${surname} ${name}`.trim();
        setValue('resourceName', doctorFullName);
      }
    },
    [isUsedAsResource],
  );
  useEffect(() => {
    registerFormDirtyStateRetriever(
      formId,
      () => Object.keys(dirtyFields).length > 0,
    );
  }, [dirtyFields]);

  const removeDoctor = useCallback(async () => {
    if (!doctorId) {
      return;
    }
    const isConfirmed = await modals.showConfirm({
      title: i18n.t('confirmation_delete_doctor_title'),
      text: i18n.t('confirmation_delete_doctor_text'),
      okButtonText: i18n.t('confirmation_delete_doctor_button'),
    });
    if (isConfirmed) {
      loadBlocking(formId, i18n.t('loading_delete'), async () => {
        try {
          await tryCompleteForm(formType, formId, async () => {
            await QueryFactory.DoctorClient.removeDoctor(doctorId);
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.DoctorQuery.getDoctorsQueryKey(),
            });
            await queryClient.invalidateQueries({
              queryKey: QueryFactory.ResourceQuery.getResourcesQueryKey(),
            });
            if (doctorDictionary) {
              await queryClient.invalidateQueries({
                queryKey: QueryFactory.DataBlockDictionaryItemQuery.getQueryKey(
                  doctorDictionary.id,
                ),
              });
            }
          });
        } catch (err: any) {
          await modals.showError({ text: err.message });
        }
      }).catch((e) => console.error('removeDoctor', e));
    }
  }, [formType, formId, tryCompleteForm, doctorDictionary]);

  const personalData = (
    <FieldBlock
      title={i18n.t('personal_data.block_title')}
      className={localStyles.fieldBlock}
    >
      <Field
        className={localStyles.dataField}
        title={i18n.t('personal_data.name')}
      >
        <Input
          {...form.register('name')}
          errorText={form.formState.errors.name?.message}
          autoFocus={true}
          autoComplete={'off'}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('personal_data.surname')}
        required={true}
      >
        <Input
          {...form.register('surname')}
          errorText={form.formState.errors.surname?.message}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('personal_data.title')}
      >
        <HookFormDropDownInput
          control={form.control}
          name={'titleId'}
          enableSearch={true}
          getOptionLabel={(item) => i18nextDict.t(item.value as any) as any}
          idFunction="id"
          options={personTitles}
          errorText={form.formState.errors.titleId?.message}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('personal_data.letter_title')}
      >
        <Input
          {...form.register('titleOfLetter')}
          errorText={form.formState.errors.titleOfLetter?.message}
        />
      </Field>
    </FieldBlock>
  );

  const contacts = (
    <FieldBlock
      title={i18n.t('contacts.block_title')}
      className={localStyles.fieldBlock}
    >
      <Field
        className={localStyles.dataField}
        title={i18n.t('contacts.mobile_phone')}
      >
        <Input
          {...form.register('mobilePhone')}
          errorText={form.formState.errors.mobilePhone?.message}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('contacts.work_phone')}
      >
        <Input
          {...form.register('workPhone')}
          errorText={form.formState.errors.workPhone?.message}
        />
      </Field>
      <Field className={localStyles.dataField} title={i18n.t('contacts.email')}>
        <Input
          {...form.register('email')}
          errorText={form.formState.errors.email?.message}
        />
      </Field>
      <Field className={localStyles.dataField} title={i18n.t('contacts.fax')}>
        <Input
          {...form.register('fax')}
          errorText={form.formState.errors.fax?.message}
        />
      </Field>
    </FieldBlock>
  );

  const addressDetails = (
    <FieldBlock
      title={i18n.t('address_details.block_title')}
      className={localStyles.fieldBlock}
    >
      <Field
        className={localStyles.dataField}
        title={i18n.t('address_details.address')}
      >
        <Input
          {...form.register('address')}
          errorText={form.formState.errors.address?.message}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('address_details.location')}
      >
        <Input
          {...form.register('location')}
          errorText={form.formState.errors.location?.message}
        />
      </Field>
      <Field
        className={styles.expandedFormPostcodeField}
        title={i18n.t('address_details.postcode')}
      >
        <Input
          {...form.register('postcode')}
          errorText={form.formState.errors.postcode?.message}
        />
      </Field>
    </FieldBlock>
  );

  const clinical = (
    <FieldBlock
      title={i18n.t('clinical.block_title')}
      className={localStyles.fieldBlock}
    >
      <Field
        className={localStyles.dataField}
        title={i18n.t('clinical.specialty')}
      >
        <HookFormDropDownInput
          control={form.control}
          name={'specialtyId'}
          enableSearch={true}
          getOptionLabel="value"
          idFunction="id"
          options={specialties}
          errorText={form.formState.errors.titleId?.message}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('clinical.clinic')}
      >
        <HookFormDropDownInput
          control={form.control}
          name={'clinicId'}
          enableSearch={true}
          getOptionLabel="value"
          idFunction="id"
          options={clinics}
          errorText={form.formState.errors.titleId?.message}
        />
      </Field>
      <Field
        className={localStyles.dataField}
        title={i18n.t('clinical.cis_id')}
      >
        <Input
          {...form.register('cisId')}
          errorText={form.formState.errors.cisId?.message}
        />
      </Field>
      <CheckBox
        {...form.register('active')}
        title={i18n.t('clinical.is_active')}
      />
    </FieldBlock>
  );

  const resourceInfo = resourceEnabled && (
    <FieldBlock
      title={i18n.t('resource.use_as_resource')}
      className={localStyles.fieldBlock}
    >
      <CheckBox
        title={i18n.t('resource.use_as_resource')}
        {...form.register('isUsedAsResource')}
      />
      <Field
        className={localStyles.dataField}
        required={!!isUsedAsResource}
        title={i18n.t('resource.resource_name')}
      >
        <Input
          {...form.register('resourceName')}
          disabled={!isUsedAsResource}
          errorText={form.formState.errors.resourceName?.message}
        />
      </Field>
    </FieldBlock>
  );

  return (
    <Loading loading={doctorQuery.isLoading}>
      <form
        className={styles.autoSizedContainer}
        onSubmit={form.handleSubmitDefault}
      >
        <div className={styles.row}>
          <div className={styles.dataColumn}>
            {personalData}
            {resourceInfo}
          </div>
          <div className={styles.dataColumn}>{contacts}</div>
          <div className={styles.dataColumn}>{addressDetails}</div>
          <div className={styles.dataColumn}>{clinical}</div>
        </div>
        <div className={styles.submitContainer}>
          {doctorId && (
            <Button
              className={styles.submit}
              title={i18n.t('control.delete')}
              onClick={removeDoctor}
              color={ButtonColor.Danger}
              width={ButtonWidth.Content}
            />
          )}
          <Error error={form.overallError} />
          <SubmitButton
            submitting={form.formState.isSubmitting}
            className={styles.submit}
            title={doctorId ? i18n.t('control.save') : i18n.t('control.create')}
          />
        </div>
      </form>
    </Loading>
  );
};
